import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';
import '@assets/js/common/table';
import { getTimerRedirectOptions, migrateTimerRedirectValue } from '../utils/pageNavigationUtils';

const PresetAmountSetting = ({ preset, presetPageMasters }) => {
    const [isLoading, setIsLoading] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetAmountPage, setPresetAmountPage] = useState(null);

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        minPrintQuantity: 1,
        maxPrintQuantity: 10,
        printPrice1cut: 4000,
        printPrice2cut: 4000,
        printPrice3cut: 4000,
        printPrice4cut: 4000,
        normalVideoPrice: 5000,
        specialVideoPrice: 6000,
        timerSeconds: 10,
        timerTimeoutRedirectUrl: 'start',
        isActive: true
    });

    // 타이머 관련
    const [timer, setTimer] = useState(10);
    const [timeOverPage, setTimeOverPage] = useState('start');
    
    // 타이머 리다이렉트 옵션
    const [timerRedirectOptions, setTimerRedirectOptions] = useState([]);

    // 페이지 데이터 로드
    useEffect(() => {
        loadAmountSelectPageData();
    }, [presetId]);
    
    // 타이머 리다이렉트 옵션 업데이트
    useEffect(() => {
        if (presetPageMasters && presetPageMasters.length > 0) {
            const options = getTimerRedirectOptions('amount', presetPageMasters);
            setTimerRedirectOptions(options);
        }
    }, [presetPageMasters]);

    // 수량 선택 페이지 데이터 로드
    const loadAmountSelectPageData = async () => {
        try {
            setIsLoading(true);
            const amountPage = await presetApi.getAmountSelectPage(presetId);
            
            setPresetAmountPage(amountPage);
            
            // 기존 timerTimeoutRedirectUrl 값을 새로운 형식으로 마이그레이션
            const migratedRedirectValue = migrateTimerRedirectValue(
                amountPage.timerTimeoutRedirectUrl, 
                'amount'
            );
            
            setFormData({
                minPrintQuantity: amountPage.minPrintQuantity ?? 1,
                maxPrintQuantity: amountPage.maxPrintQuantity ?? 10,
                printPrice1cut: amountPage.printPrice1cut ?? 4000,
                printPrice2cut: amountPage.printPrice2cut ?? 4000,
                printPrice3cut: amountPage.printPrice3cut ?? 4000,
                printPrice4cut: amountPage.printPrice4cut ?? 4000,
                normalVideoPrice: amountPage.normalVideoPrice ?? 5000,
                specialVideoPrice: amountPage.specialVideoPrice ?? 6000,
                timerSeconds: amountPage.timerSeconds ?? 10,
                timerTimeoutRedirectUrl: migratedRedirectValue,
                isActive: amountPage.isActive ?? true
            });
            
            setTimer(amountPage.timerSeconds ?? 10);
            setTimeOverPage(migratedRedirectValue);
        } catch (error) {
            console.error('수량 선택 페이지 데이터 로드 실패:', error);
            // 에러 시 기본값 유지
        } finally {
            setIsLoading(false);
        }
    };

    // 수량 조절 핸들러
    const handleQuantityChange = (type, action) => {
        let currentValue = formData[type];
        let newValue;
        
        if (action === 'increase') {
            if (type === 'minPrintQuantity') {
                newValue = Math.min(currentValue + 1, formData.maxPrintQuantity);
            } else if (type === 'maxPrintQuantity') {
                newValue = Math.min(currentValue + 1, 99);
            }
        } else {
            if (type === 'minPrintQuantity') {
                newValue = Math.max(currentValue - 1, 1);
            } else if (type === 'maxPrintQuantity') {
                newValue = Math.max(currentValue - 1, formData.minPrintQuantity);
            }
        }
        
        setFormData({
            ...formData,
            [type]: newValue
        });
    };

    // 가격 변경 핸들러
    const handlePriceChange = (type, value) => {
        const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
        setFormData({
            ...formData,
            [type]: numericValue
        });
    };

    // 타이머 조절 핸들러
    const handleTimerChange = (action) => {
        let newTimer;
        if (action === 'increase') {
            newTimer = Math.min(timer + 1, 60);
        } else {
            newTimer = Math.max(timer - 1, 1);
        }
        
        setTimer(newTimer);
        setFormData({
            ...formData,
            timerSeconds: newTimer
        });
    };

    // 타임 오버 페이지 변경 핸들러
    const handleTimeOverPageChange = (e) => {
        const newPage = e.target.value;
        setTimeOverPage(newPage);
        setFormData({
            ...formData,
            timerTimeoutRedirectUrl: newPage
        });
    };

    // 저장 이벤트 리스너
    useEffect(() => {
        const eventName = `savePagePreset_amount`;

        const saveHandler = async (event) => {
            if (!preset?.id) {
                alert("프리셋 정보가 없습니다.");
                return;
            }

            try {
                setIsLoading(true);

                const dataToSave = {
                    presetId: event.detail.presetId,
                    minPrintQuantity: formData.minPrintQuantity,
                    maxPrintQuantity: formData.maxPrintQuantity,
                    printPrice1cut: formData.printPrice1cut,
                    printPrice2cut: formData.printPrice2cut,
                    printPrice3cut: formData.printPrice3cut,
                    printPrice4cut: formData.printPrice4cut,
                    normalVideoPrice: formData.normalVideoPrice,
                    specialVideoPrice: formData.specialVideoPrice,
                    timerSeconds: formData.timerSeconds,
                    timerTimeoutRedirectUrl: formData.timerTimeoutRedirectUrl,
                    isActive: formData.isActive
                };
                
                const response = await presetApi.saveAmountSelectPage(event.detail.presetId, dataToSave);
                console.log("수량 선택 페이지 저장 성공:", response);

                // 저장 후 최신 데이터로 화면 갱신
                if (response) {
                    setFormData(response);
                    setPresetAmountPage(response);
                }

            } catch (error) {
                console.error("수량 선택 페이지 저장 실패:", error);
                alert("수량 선택 페이지 저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        document.addEventListener(eventName, saveHandler);

        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [preset?.id, formData]);

    if (isLoading) {
        return <div className="preset-settings">로딩 중...</div>;
    }

    return (
        <div className="preset-settings">
            {/* 인쇄 수량 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">인쇄 수량 설정</h4>
                <div className="settings-row">
                    <div className="settings-label">최소 수량</div>
                    <div className="quantity-control-container">
                        <button 
                            className="quantity-btn" 
                            onClick={() => handleQuantityChange('minPrintQuantity', 'decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="quantity-input" 
                            value={formData.minPrintQuantity}
                            readOnly
                        />
                        <button 
                            className="quantity-btn" 
                            onClick={() => handleQuantityChange('minPrintQuantity', 'increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="quantity-unit">매</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">최대 수량</div>
                    <div className="quantity-control-container">
                        <button 
                            className="quantity-btn" 
                            onClick={() => handleQuantityChange('maxPrintQuantity', 'decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="quantity-input" 
                            value={formData.maxPrintQuantity}
                            readOnly
                        />
                        <button 
                            className="quantity-btn" 
                            onClick={() => handleQuantityChange('maxPrintQuantity', 'increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="quantity-unit">매</span>
                    </div>
                </div>
            </div>

            {/* 인쇄 가격 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">인쇄 가격 설정</h4>
                <div className="settings-row">
                    <div className="settings-label">1컷</div>
                    <div className="price-input-container">
                        <input 
                            type="text" 
                            className="price-input" 
                            value={formData.printPrice1cut.toLocaleString()}
                            onChange={(e) => handlePriceChange('printPrice1cut', e.target.value)}
                        />
                        <span className="price-unit">원</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">2컷</div>
                    <div className="price-input-container">
                        <input 
                            type="text" 
                            className="price-input" 
                            value={formData.printPrice2cut.toLocaleString()}
                            onChange={(e) => handlePriceChange('printPrice2cut', e.target.value)}
                        />
                        <span className="price-unit">원</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">3컷</div>
                    <div className="price-input-container">
                        <input 
                            type="text" 
                            className="price-input" 
                            value={formData.printPrice3cut.toLocaleString()}
                            onChange={(e) => handlePriceChange('printPrice3cut', e.target.value)}
                        />
                        <span className="price-unit">원</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">4컷</div>
                    <div className="price-input-container">
                        <input 
                            type="text" 
                            className="price-input" 
                            value={formData.printPrice4cut.toLocaleString()}
                            onChange={(e) => handlePriceChange('printPrice4cut', e.target.value)}
                        />
                        <span className="price-unit">원</span>
                    </div>
                </div>
            </div>

            {/* 동영상 가격 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">동영상 가격 설정</h4>
                <div className="settings-row">
                    <div className="settings-label">일반 동영상</div>
                    <div className="price-input-container">
                        <input 
                            type="text" 
                            className="price-input" 
                            value={formData.normalVideoPrice.toLocaleString()}
                            onChange={(e) => handlePriceChange('normalVideoPrice', e.target.value)}
                        />
                        <span className="price-unit">원</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">스페셜 동영상</div>
                    <div className="price-input-container">
                        <input 
                            type="text" 
                            className="price-input" 
                            value={formData.specialVideoPrice.toLocaleString()}
                            onChange={(e) => handlePriceChange('specialVideoPrice', e.target.value)}
                        />
                        <span className="price-unit">원</span>
                    </div>
                </div>
            </div>

            {/* 타이머 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="timer-input" 
                            value={timer}
                            readOnly
                        />
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">타임 오버 이동 페이지</div>
                    <div className="page-select-container">
                        <select 
                            className="page-select"
                            value={timeOverPage}
                            onChange={handleTimeOverPageChange}
                        >
                            {timerRedirectOptions.map(option => (
                                <option 
                                    key={option.value} 
                                    value={option.value}
                                    disabled={!option.available}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PresetAmountSetting;
